import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import type { FieldArrayRenderProps, FormikProps } from 'formik';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { AutoCompleteInputWired } from 'src/app/components/Form/AutocompleteInput/container';
import { DoctorRequiredMedications } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/DoctorRequiredMedications';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { inputFieldBlur, inputFieldFocused, searchForMedications } from 'src/app/state/actions';
import type { Dispatch } from 'src/app/state/store';
import type { Medication } from 'src/app/types';
import type { FORM_INPUT_ICON_STRINGS } from 'src/consts';

import { getMedsRequiringDoctorTransfer, getTransferableMeds } from '../utils';
import { EMPTY_MEDICATION } from '../validation';

import { MedicationChip } from './MedicationChip';
import { MedicationChipContainer, MedicationsChipListContainer } from './MedicationChip/styles';
import { MedSearchItem } from './styles';
import styles from './styles.module.scss';

const MED_INDEX = 0;

const itemToString = item => {
  if (item?.primaryName) {
    return item?.primaryName;
  }
  return item?.attributes?.primaryName;
};

const MenuItem = ({ item }) => {
  if (item?.primaryName) {
    const isEligible = item.eligibleForTransfer === true;

    return (
      <MedSearchItem ineligible={!isEligible}>
        {item.primaryName}
        {isEligible ? '' : ' — Unavailable'}
      </MedSearchItem>
    );
  }
  return <div>{item?.attributes?.primaryName}</div>;
};

const renderCustomEntry = ({ item }, newEntryMessage) => (
  <MedSearchItem>
    <span>"{item.name}"</span>
    <span className={styles.newEntrySuffix}>&nbsp;{`- ${newEntryMessage}`} </span>
  </MedSearchItem>
);

const mapDispatchToProps = (dispatch: Dispatch) => ({
  searchForMedications: async (searchTerm: string, zipCode?: string) => {
    const res = await dispatch(searchForMedications(searchTerm, zipCode));
    return res.items.medications;
  },
  onFocus: async (medIndex: number) => dispatch(inputFieldFocused(medIndex)),
  onBlur: async (medIndex: number) => dispatch(inputFieldBlur(medIndex)),
});

const connector = connect(undefined, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface MedicationsFieldArrayProps
  extends PropsFromRedux,
    Pick<FormikProps<{ [key: `medications.${number}`]: typeof EMPTY_MEDICATION }>, 'setFieldValue'> {
  medicationsArrayHelpers: FieldArrayRenderProps;
  medications: typeof EMPTY_MEDICATION[];
  fieldArrayName: string;
  iconString?: FORM_INPUT_ICON_STRINGS;
  saveMedications?: () => void;
  disabled?: boolean;
  zipCode?: string;
  isTransferForm?: boolean;
}

/**
 * This component was originally designed to host multiple comboboxes. It would start with 1 and
 * 1 more would be rendered every time a user selected an option from the dropdown results.
 *
 * A lot of the code is just hacks and duct tape to avoid working on its consuming context within
 * a Formik FieldArray component. FieldArray was correctly used when we had many, conditionally rendered
 * fields, but its usage now is just a hack.
 *
 * Now, we render a single visible combobox, but abuse Formik to store the state of all selected medications.
 *
 * A future refactor can probably use `AutoCompleteInputWired` within the design system `TextField`
 * component and all of the complex logic can be moved to the func passed to `onSelectedItemChangeCallback`.
 */
export const UnconnectedMedicationsFieldArray = ({
  disabled,
  fieldArrayName,
  iconString,
  medications,
  medicationsArrayHelpers,
  onBlur,
  onFocus,
  saveMedications,
  searchForMedications,
  setFieldValue,
  zipCode,
  isTransferForm,
}: MedicationsFieldArrayProps) => {
  const [ineligbleMedsSeen, setIneligibleMedsSeen] = useState<string[]>([]);
  const trackMedSelect = useSegmentEvent('selectTransferMedication', { shouldTrackOnMount: false });
  const trackViewedIneligibleMed = useSegmentEvent('viewTransferIneligibleMeds', { shouldTrackOnMount: false });

  const actualInputFieldId = `${fieldArrayName}.${MED_INDEX}`;

  /**
   * This component cares about Formik context, but we store RELEVANT data in Redux. That means
   * when we submit past this form, Redux is synced with everything except the EMPTY_MEDICATION
   * necessary for this FieldArray component to work. See top-of-component comment on why this
   * shouldn't even be a FieldArray component... Regardless, a problem occurs which requires this
   * hook to fix. Namely, if we _return_ to this form from a later step, the form will be
   * initialized with Redux data... devoid of the EMPTY_MEDICATION. So, the next addition or
   * removal of medication will replace a legitimately added medication. This hook ensures that
   * when we return to this form after a previous submission, we add the EMPTY_MEDICATION back to
   * the beginning of the array, allowing add and remove to work as expected.
   */
  useEffect(() => {
    if (medications && medications[0] && medications[0].attributes?.primaryName) {
      medicationsArrayHelpers.unshift(EMPTY_MEDICATION);
    }
  }, [medications, medicationsArrayHelpers]);

  const medsRequiringDoctorTransfer = getMedsRequiringDoctorTransfer(medications);

  useEffect(() => {
    return () => {
      if (ineligbleMedsSeen.length > 0) {
        trackViewedIneligibleMed({ medication_names: ineligbleMedsSeen });
      }
    };
  }, [ineligbleMedsSeen, trackViewedIneligibleMed]);

  const id = `${actualInputFieldId}.name`;

  const resetTextField = () => setFieldValue(actualInputFieldId, EMPTY_MEDICATION);

  const addMedication = medication => {
    const allMedNames = medications.map(({ attributes }) => attributes?.primaryName ?? '');
    const hasMedAddedAlready = allMedNames.includes(medication.primaryName);

    if (hasMedAddedAlready) {
      resetTextField();
    } else {
      const selectedMed = {
        name: medication.primaryName,
        attributes: { ...medication },
        id: uuid(),
      };

      setFieldValue(`${fieldArrayName}.${medications.length}`, selectedMed);
      resetTextField();
      saveMedications?.();
      trackMedSelect({
        eligible: !medication.doctorRequiredForTransfer,
        medication_name: medication.primaryName,
      });
    }
  };

  const removeMedication = (medicationPrimaryName: string) => () => {
    const relevantIndex = medications.findIndex(med => med.name === medicationPrimaryName);
    medicationsArrayHelpers.remove(relevantIndex);
    resetTextField();
  };

  const transferableMeds = useMemo(() => getTransferableMeds(medications), [medications]);

  return (
    <div>
      <div
        className={classNames(styles.autocomplete, styles.fullWidth)}
        css={
          medications.length > 0
            ? undefined
            : css`
                margin-bottom: ${({ theme }) => theme.space.s7};

                ${({ theme }) => theme.mediaQueries.smallDown} {
                  margin-bottom: ${({ theme }) => theme.space.s6};
                }
              `
        }
      >
        <AutoCompleteInputWired
          id={id}
          name={id}
          label="Medication"
          searchHelperText={'Search a medication name'}
          itemToString={itemToString}
          renderMenuItem={MenuItem}
          compactStyle
          onFocus={() => onFocus(MED_INDEX)}
          onBlur={() => onBlur(MED_INDEX)}
          fetchItemsOnChange={async searchTerm => {
            const result = await searchForMedications(searchTerm, zipCode);
            const ineligibleMeds = result.filter(med => med.eligibleForTransfer === false).map(med => med.primaryName);
            setIneligibleMedsSeen(prev => [...new Set([...prev, ...ineligibleMeds])]);
            return result;
          }}
          onSelectedItemChangeCallback={addMedication}
          renderCustomNewEntry={renderCustomEntry}
          iconString={iconString}
          disabled={disabled}
          isTransferForm={isTransferForm}
        />
      </div>

      {transferableMeds.length > 0 && (
        <MedicationsChipListContainer
          css={css`
            margin-top: ${({ theme }) => theme.space.s4};
          `}
        >
          {transferableMeds.map(med => {
            const primaryName = med.attributes?.primaryName as string;

            return (
              <MedicationChipContainer key={primaryName}>
                <MedicationChip title={primaryName} onClick={removeMedication(primaryName)} />
              </MedicationChipContainer>
            );
          })}
        </MedicationsChipListContainer>
      )}

      {medsRequiringDoctorTransfer.length > 0 && (
        <DoctorRequiredMedications medications={medsRequiringDoctorTransfer as Medication[]} />
      )}
    </div>
  );
};

export const MedicationsFieldArray = connector(UnconnectedMedicationsFieldArray);
